import React, { useState, useMemo } from 'react';
import { Box, Typography, List, ListItemButton, ListItemAvatar, Avatar, ListItemText, TextField, InputAdornment, IconButton, Tooltip, Divider } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CircleIcon from '@mui/icons-material/Circle';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < (str || '').length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  const colors = ['#075e54', '#0a8a7a', '#128C7E', '#25d366', '#34b7f1', '#e91e63', '#9c27b0', '#ff9800'];
  return colors[Math.abs(hash) % colors.length];
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const diff = now - d;
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  if (days === 0) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (days === 1) return 'Yesterday';
  if (days < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { day: '2-digit', month: 'short', year: 'numeric' });
};

const ChatSidebar = ({ conversations, selectedSession, onSelect, loading, onRefresh, onExportAll, refreshing, exporting }) => {
  const [searchText, setSearchText] = useState('');

  const filtered = useMemo(() => {
    if (!searchText.trim()) return conversations;
    const q = searchText.toLowerCase();
    return conversations.filter(c =>
      (c.userName || '').toLowerCase().includes(q) ||
      (c.sessionId || '').toLowerCase().includes(q)
    );
  }, [conversations, searchText]);

  return (
    <Box sx={{
      width: 360, borderRight: '1px solid #e0e0e0', bgcolor: '#fff',
      display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0,
      boxShadow: '2px 0 8px rgba(0,0,0,0.04)',
    }}>
      {/* Header — fixed */}
      <Box sx={{ px: 2, py: 1.8, bgcolor: '#075e54', color: '#fff', flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 16, letterSpacing: '-0.3px' }}>Inbox</Typography>
          <Typography variant="caption" sx={{ opacity: 0.7, fontSize: 11, fontWeight: 500 }}>
            {conversations.length} {conversations.length === 1 ? 'chat' : 'chats'}
          </Typography>
        </Box>
      </Box>

      {/* Search + Actions — fixed */}
      <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        <TextField
          placeholder="Search.."
          size="small" fullWidth value={searchText}
          onChange={e => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment>,
            sx: {
              borderRadius: 2.5, fontSize: 13, bgcolor: '#f0f2f5', height: 38,
              '& fieldset': { border: 'none' },
              '&:hover': { bgcolor: '#e8eaed' },
              transition: 'all 0.2s ease',
            },
          }}
          sx={{ '& .Mui-focused': { '& .MuiInputBase-root': { bgcolor: '#e8eaed' } } }}
        />
        <Tooltip title="Refresh chats" arrow>
          <IconButton size="small" onClick={onRefresh} disabled={refreshing}
            sx={{ color: '#075e54', bgcolor: '#f0f2f5', borderRadius: 1.5, width: 34, height: 34, '&:hover': { bgcolor: '#dcf8c6', transform: 'scale(1.05)' }, transition: 'all 0.15s ease' }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Export all chats" arrow>
          <IconButton size="small" onClick={onExportAll} disabled={exporting}
            sx={{ color: '#075e54', bgcolor: '#f0f2f5', borderRadius: 1.5, width: 34, height: 34, '&:hover': { bgcolor: '#dcf8c6', transform: 'scale(1.05)' }, transition: 'all 0.15s ease' }}>
            <DownloadIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Conversations List */}
      <List sx={{ flex: 1, overflow: 'auto', minHeight: 0, py: 0, '&::-webkit-scrollbar': { width: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#c1c1c1', borderRadius: 2 } }}>
        {loading && conversations.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>Loading...</Typography>
        ) : filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            {searchText ? 'No matching conversations' : 'No conversations yet'}
          </Typography>
        ) : (
          filtered.map((conv) => {
            const sel = selectedSession === conv.sessionId;
            const lastMsg = conv.lastMessage || '';
            const initials = getInitials(conv.userName);
            const color = stringToColor(conv.sessionId);
            const hasUnread = conv.unreadCount > 0;
            return (
              <React.Fragment key={conv.sessionId}>
                <ListItemButton
                  selected={sel}
                  onClick={() => onSelect(conv.sessionId)}
                  sx={{
                    py: 1.4, px: 1.5, alignItems: 'flex-start',
                    bgcolor: sel ? '#dcf8c6' : 'transparent',
                    '&:hover': { bgcolor: sel ? '#dcf8c6' : '#f5f5f5' },
                    '&.Mui-selected': { bgcolor: '#dcf8c6' },
                    transition: 'all 0.15s ease',
                    borderLeft: sel ? '3px solid #075e54' : '3px solid transparent',
                    position: 'relative',
                  }}
                >
                  <ListItemAvatar sx={{ mt: 0.2 }}>
                    <Avatar sx={{ bgcolor: color, width: 45, height: 45, fontSize: 15, fontWeight: 700, boxShadow: sel ? '0 0 0 2px #075e54' : 'none', transition: 'box-shadow 0.15s ease' }}>{initials}</Avatar>
                  </ListItemAvatar>
                  <Box sx={{ flex: 1, minWidth: 0, ml: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" fontWeight={hasUnread ? 700 : 600} fontSize={14} color={conv.blocked ? '#94a3b8' : '#1e293b'} noWrap sx={{ maxWidth: 150 }}>
                        {conv.userName || conv.sessionId}
                      </Typography>
                      {conv.blocked && (
                        <Box sx={{
                          bgcolor: '#fee2e2', borderRadius: '8px', px: 0.7, py: 0.15, flexShrink: 0,
                          border: '1px solid #fecaca',
                        }}>
                          <Typography variant="caption" fontSize={10} fontWeight={700} color="#dc2626">
                            Blocked
                          </Typography>
                        </Box>
                      )}
                      <Typography variant="caption" color="#94a3b8" fontSize={11} sx={{ flexShrink: 0, ml: 1 }}>
                        {formatDate(conv.updatedAt || conv.createdAt)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                      {hasUnread && <CircleIcon sx={{ fontSize: 8, color: '#25d366', flexShrink: 0 }} />}
                      <Typography variant="caption" color={hasUnread ? '#1e293b' : '#64748b'} noWrap
                        fontWeight={hasUnread ? 600 : 400} fontSize={12} sx={{ flex: 1 }}>
                        {lastMsg.slice(0, 60) || 'No messages'}
                      </Typography>
                      {hasUnread && (
                        <Box sx={{
                          bgcolor: '#25d366', borderRadius: '10px', minWidth: 20, height: 20,
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                          px: 0.5, boxShadow: '0 1px 2px rgba(37,211,102,0.3)',
                        }}>
                          <Typography variant="caption" fontSize={10} fontWeight={700} color="#fff">
                            {conv.unreadCount > 99 ? '99+' : conv.unreadCount}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </ListItemButton>
                <Divider sx={{ ml: 7.5, mr: 1.5, borderColor: '#f0f0f0' }} />
              </React.Fragment>
            );
          })
        )}
      </List>
    </Box>
  );
};

export default ChatSidebar;
